/**
 * MUI Table component that replaces the styled-components Table component
 */
import React, { useEffect, useState, useMemo, useRef, useLayoutEffect, useContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import ReducersIndex from 'reduxModules/ducks';
import { getConditionResult, getCurrentValue2 } from "reduxModules/selectors/containerSelectors";
import { getDrawingBox, getScale } from "reduxModules/ducks/ui";
import ToolContext from "components/services/ToolContext";
import { Chair } from "globalConstants/Icons/Furniture";
import { Move, Rotate } from "globalConstants/Icons/Actions";
import { iconThemes } from "globalConstants/Themes";
import useMousePosition from "hooks/useMousePosition";
import { mapValues, clamp, round, floor, range, assignWith, isEmpty, every, keys } from "lodash";
import MuiTableConstants from "config/tables/tableConstants";
import { useTheme } from "theme/ThemeContext";
import { 
  TableCenter, 
  TableTransform, 
  CurrentTable, 
  TableElements,
  ChairAttributes,
  MuiTableProps,
  ChairProperties
} from "types/components/elements";
import { RootState } from "types/redux/state";

/**
 * MUI Table component
 */
const MuiTable: React.FC<MuiTableProps> = (props) => {
  const areaId = useSelector((state: RootState) => getCurrentValue2(state, 'activeTab'));
  const { bbox, scale } = useSelector(getDrawingBox);
  const currentTable = useSelector((state: RootState) => getCurrentValue2(state, 'table')) as CurrentTable;
  const tableSize = mapValues(currentTable.size, (v) => v * scale);
  const chair: ChairProperties = mapValues(MuiTableConstants.chair, (v) => typeof v === 'function' ? v : v * scale) as ChairProperties;
  const context = useContext(ToolContext);
  const table2wall = context.constants.area.table2wall * scale;
  const [tableCenter, setTableCenter] = useState<TableCenter>({ x: 0, y: 0 });
  const [tableTransform, setTableTransfrom] = useState<TableTransform>({ x: 0, y: 0, a: "0 0 0" });
  const [moving, setMoving] = useState<boolean>(false);
  const [rotating, setRotating] = useState<boolean>(false);
  const [rotationStart, setRotationStart] = useState<number>(0);
  const [offset, setOffset] = useState<{ x: number, y: number }>({ x: 0, y: 0 });
  const mouse = useMousePosition(true, true);
  const moveIconRef = useRef<SVGGElement>(null);
  const rotateIconRef = useRef<SVGGElement>(null);
  
  const { 
    notched = false, 
    maxSlope = null, 
    pathProps = () => ({}), 
    chairs = () => ({ chairArray: [], chairSpacing: { x: 0, y: 0 } }), 
    devicePathProps = () => ({}) 
  } = MuiTableConstants.tables[currentTable.shape] ?? {};
  
  const { endTable = false, minimum: minimumTableSize = {length: 0, width: 0} } = context.constants.tables[currentTable.shape] ?? {};

  // Get data related to tablemic to create the mic path
  const microphones = useSelector((state: RootState) => getCurrentValue2(state, 'microphones'));
  const expansionMicType = useSelector((state: RootState) => getCurrentValue2(state, 'expansionMicType'));
  const { model: micModel } = microphones || { model: 'ceilingmic' };
  // When a table expansion mic is configured alongside a bar, generate the table mic path
  // using ttm_x parameters (same as a standalone table mic) so calcExpansionTableMicPlacement
  // samples a correctly-shaped path. Without this, the bar's larger coverage radius would
  // distort the path used to position expansion table mics around the table edge.
  const microphoneModel = expansionMicType === 'table' ? 'ttm_x' : micModel.replace('mic', '_mic');
  const speakers = useSelector((state: RootState) => getCurrentValue2(state, 'speakers'));
  const { model, height } = speakers || { model: 'ceilingspkr', height: 2.7 };

  const {
    equipment: {
      microphones: { [microphoneModel]: { micData } },
      speakers: { [model]: { coverageAngle } },
      micCategories: { TABLETOP }
    },
    acoustics,
    area: { talkerHeight }
  } = context.constants;
  const isTableMic = expansionMicType === 'table' || TABLETOP.has(micModel);
  // Only the expansion TABLE mic keeps the tapered #micPath (see keystone/vshape
  // devicePathProps). Ceiling mics must let the flat-centreline collapse run.
  const isExpansionTableMic = expansionMicType === 'table';
  
  const currentAcoustics = useSelector((state: RootState) => getCurrentValue2(state, 'acoustics'));
  const micCoverageRadius = micData[acoustics.indexOf(currentAcoustics)] * scale;
  // KNOWN BUG, left as-is: this is missing parens around `height - talkerHeight` (compare to the
  // working formula in roomCalculations.ts: `(expSpkrHeight - talkerHeight) * Math.tan(...)`), so
  // it actually evaluates as `height - (talkerHeight * Math.tan(...))`. Combined with a 90°
  // coverageAngle on at least one speaker model (where Math.tan blows up to ~1.6e16 due to
  // floating-point imprecision around the asymptote), this can produce a nonsensical huge negative
  // number instead of a real coverage radius. Currently harmless: `generateDevicePath` below always
  // passes `pathBox: null` for speakers (`isSpeaker ? null : tableBox`), and every shape's
  // `devicePathProps` (tableConstants.ts) only reads its `deviceCoverageRadius` argument inside an
  // `!isEmpty(tableBox)` guard — so this value never reaches any branch that affects the rendered
  // spkrPath. It would only matter if a future change stops hardcoding that `null` (e.g. to add the
  // same "collapse the path when the table is too small for the coverage circle" safeguard mics
  // already have) — at which point this garbage value would silently defeat that check, since a
  // huge negative number can never satisfy the `>= tableBox.x/y` comparison those guards use.
  const spkrCoverageRadius = (height - talkerHeight * Math.tan(coverageAngle * Math.PI / 180));

  const dispatch = useDispatch();

  useLayoutEffect(() => {
    // Calculate the center of the table - using the exact same calculation as the original Table.js
    const cx = round(tableSize.length * 2) / 4;
    const cy = round(tableSize.width * 2) / 4;
    setTableCenter({ x: cx, y: cy });

    // Calculate the position of the table in the canvas
    // bbox.cx and bbox.cy are the center coordinates of the canvas
    const tx = bbox.cx - cx + currentTable.transform.x * scale;
    const ty = bbox.cy - cy + currentTable.transform.y * scale;
    const ta = `${currentTable.transform.a} ${cx} ${cy}`;
    setTableTransfrom({ x: tx, y: ty, a: ta });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTable.shape, currentTable.size.length, currentTable.size.width, currentTable.transform, bbox.cx, bbox.cy, scale]);

  const { corner = null, leftCorner = null, rightCorner = null, length = tableSize.length, bow = null, width = tableSize.width } = pathProps(tableSize);

  // Calculate the tablebox - used for tabletop mic calculations 
  const tableBox = useMemo(() => ({
    x: tableSize.length + 2 * (chair.size - chair.clearance),
    y: tableSize.width + 2 * (chair.size - chair.clearance)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [tableSize.length, tableSize.width]);

  // Generate the table
  const table = useMemo((): TableElements => {
    // Generate the Table path
    const slope = currentTable.shape === 'ushape' ? 0 : 
      clamp((tableSize.width / 2 - 0.4572 * scale) / length, 0, maxSlope || Number.MAX_VALUE);
    const legWidth = 0.6096 * scale;
    const legLength = length - legWidth;
    const x0 = tableCenter.x - length / 2;
    const x1 = slope ? (width / 2 - legWidth + (corner as number) + slope * x0) / slope : x0;
    const innerTapper = slope * Math.min(x1 - x0, legLength);
    const innerCorner = maxSlope && (x1 - x0 <= legLength) ? 0 : clamp(width - legWidth * 2 + (corner as number) * 2, 0, corner as number) / 2;
    
    // Calculate common variables needed for all table types
    const tapper = maxSlope ? slope * length : 0;
    const rightWidth = maxSlope ? width - tapper * 2 : rightCorner ? 0 : null;
    const originX = tableCenter.x - (length / 2) - (rightCorner as number) / 2 + (leftCorner as number) / 2;
    const originY = tableCenter.y - (width / 2) - ((leftCorner ?? corner) as number);
    
    const { chairArray = [], chairSpacing = {} } = chairs(tableSize, chair, slope);
    
    // HELPER function to generate device paths
    const generateDevicePath = (isTableMic = false, isSpeaker = false) => {
      const deviceFromEdge = (MuiTableConstants.deviceToEdge(isTableMic, acoustics.indexOf(currentAcoustics)) - (isSpeaker ? .865 : 0)) * scale;

      // Get coverage radius based on device type
      const coverageRadius = isSpeaker ? spkrCoverageRadius : micCoverageRadius;

      // Get device path properties
      const pathBox = isSpeaker ? null : tableBox;

      const {
        devicePathLength = 0,
        devicePathWidth = 0,
        devicePathCorner = 0,
        devicePathRightCorner = 0,
        devicePathBow = 0
      } = devicePathProps(tableSize, chairSpacing, pathBox, deviceFromEdge, coverageRadius, legWidth, isExpansionTableMic) ?? {};

      // Calculate path parameters
      const devicePathTapper = maxSlope && devicePathWidth !== 0 ? slope * devicePathLength : 0;

      // Adjust parameters based on device type
      let devicePathRightWidth;
      if (isSpeaker) {
        devicePathRightWidth = (maxSlope ? devicePathWidth - devicePathTapper * 2 : 0) + chair.size;
      } else {
        devicePathRightWidth = maxSlope ? devicePathWidth - devicePathTapper * 2 : 0;
      }

      // Calculate origin points
      let devicePathOriginX, devicePathOriginY;

      if (isSpeaker) {
        devicePathOriginX = endTable
          ? tableCenter.x - devicePathLength / 2 + chair.size / 2
          : tableCenter.x + devicePathLength / 2 + (devicePathRightCorner ?? devicePathCorner);

        // Was `deviceFromEdge - chair.size / 2` — an absolute value with no `tableCenter.y` term,
        // so the path's vertical start was anchored near the SVG's local origin instead of the
        // table's actual position, leaving an asymmetric inset from the table's two long edges
        // (small on one side, large on the other) once devicePathWidth is more than marginal —
        // most visible on wide two-armed shapes (ushape/boat) where the return leg lands well
        // inside the table instead of on it. Center on `tableCenter.y` like `table.path`'s own
        // `originY` does, keeping the existing chair.size/2 speaker-specific nudge.
        devicePathOriginY = endTable && (devicePathRightCorner !== 0 || devicePathTapper !== 0 || devicePathWidth !== 0)
          ? tableCenter.y - devicePathWidth / 2 - chair.size / 2
          : tableCenter.y;
      } else {
        devicePathOriginX = endTable
          ? tableCenter.x - devicePathLength / 2 - devicePathRightCorner / 2
          : tableCenter.x + devicePathLength / 2 + (devicePathRightCorner ?? devicePathCorner);

        // See the isSpeaker branch's comment above — same missing `tableCenter.y` term, same fix.
        devicePathOriginY = endTable && (devicePathRightCorner !== 0 || devicePathTapper !== 0 || devicePathWidth !== 0)
          ? tableCenter.y - devicePathWidth / 2
          : tableCenter.y;
      }

      // Generate path string
      const path = [`m${devicePathOriginX} ${devicePathOriginY}`];
      if (endTable) {
        path.push(...[
          `q${devicePathLength / 2},${devicePathBow ? -devicePathBow : devicePathTapper / 2} ${devicePathLength},${devicePathTapper}`,
          `a${devicePathRightCorner ?? devicePathCorner},${devicePathRightCorner ?? devicePathCorner} 0 0 1 ${devicePathRightCorner ?? devicePathCorner},${devicePathRightCorner ?? devicePathCorner}`,
          `v${devicePathRightWidth > 0 ? devicePathRightWidth : devicePathWidth}`,
          `a${devicePathRightCorner ?? devicePathCorner},${devicePathRightCorner ?? devicePathCorner} 0 0 1 -${devicePathRightCorner ?? devicePathCorner},${devicePathRightCorner ?? devicePathCorner}`,
          `q${-devicePathLength / 2},${devicePathBow ? devicePathBow : devicePathTapper / 2} ${-devicePathLength},${devicePathTapper}`,
        ]);
      } else {
        path.push(...[
          `v${devicePathWidth / 2}`,
          `a${devicePathRightCorner ?? devicePathCorner},${devicePathRightCorner ?? devicePathCorner} 0 0 1 -${devicePathRightCorner ?? devicePathCorner},${devicePathRightCorner ?? devicePathCorner}`,
          `q${-devicePathLength / 2},${devicePathBow} ${-devicePathLength},${0}`,
          `a${devicePathCorner},${devicePathCorner} 0 0 1 -${devicePathCorner},-${devicePathCorner}`,
          `v${-devicePathWidth}`,
          `a${devicePathCorner},${devicePathCorner} 0 0 1 ${devicePathCorner},-${devicePathCorner}`,
          `q${devicePathLength / 2},${-devicePathBow} ${devicePathLength},${0}`,
          `a${devicePathRightCorner ?? devicePathCorner},${devicePathRightCorner ?? devicePathCorner} 0 0 1 ${devicePathRightCorner ?? devicePathCorner},${devicePathRightCorner ?? devicePathCorner}`,
          `z`
        ]);
      }

      return path.join(' ');
    };
    

    const inner = (notched && width >= (legWidth - (corner as number)) * 2 && innerTapper >= 0) ? [
      `v${(corner as number) * 2 - legWidth}`,
      `a${corner},${corner} 0 0 1 ${corner},-${corner}`,
      `l${Math.min(legLength + innerCorner, maxSlope ? x1 - x0 : length)} ${-innerTapper}`,
      `a${innerCorner},${innerCorner} 0 0 0 ${innerCorner},-${innerCorner}`,
      `v${maxSlope && (x1 - x0 <= legLength) ? 0 : (legWidth - (corner as number) + innerCorner + innerTapper) * 2 - width}`,
      `a${innerCorner},${innerCorner} 0 0 0 -${innerCorner},-${innerCorner}`,
      `l${-Math.min(legLength + innerCorner, maxSlope ? x1 - x0 : length)} ${-innerTapper}`,
      `a${corner},${corner} 0 0 1 -${corner},-${corner}`,
      `v${(corner as number) * 2 - legWidth}`] :
      [`v${- width}`];

    const path = [
      `m${originX} ${originY}`,
      `q${length / 2},${bow ? -bow : tapper / 2} ${length},${tapper}`,
      `a${rightCorner ?? corner},${rightCorner ?? corner} 0 0 1 ${rightCorner ?? corner},${rightCorner ?? corner}`,
      `v${rightWidth ?? width}`,
      `a${rightCorner ?? corner},${rightCorner ?? corner} 0 0 1 -${rightCorner ?? corner},${rightCorner ?? corner}`,
      `q${-length / 2},${bow ?? tapper / 2} ${-length},${tapper}`,
      `a${leftCorner ?? corner},${leftCorner ?? corner} 0 0 1 -${leftCorner ?? corner},-${leftCorner ?? corner}`,
      `${inner.join()}`,
      `a${leftCorner ?? corner},${leftCorner ?? corner} 0 0 1 ${leftCorner ?? corner},-${leftCorner ?? corner}z`,
    ].join();

    const remove = currentTable.ghostChairs
      .filter(chair => !chairArray.map(x => x.id).includes(chair));
    
    // Use type assertion for the container actions since setTable may not be fully typed in the reducer
    remove.forEach(chair => {
      // Handle setTable action with type assertion to avoid TS errors
      dispatch((ReducersIndex.container as any).setTable({ chair }));
    });

    return {
      path,
      chairs: chairArray,
      micPath: generateDevicePath(true, false),
      spkrPath: generateDevicePath(false, true)
    };
    // currentAcoustics/spkrCoverageRadius: generateDevicePath's `deviceFromEdge` (the acoustics-tier
    // deviceToEdge lookup) and its speaker-specific coverageRadius both close over these, but
    // neither was listed here — this memo only recomputed when `micCoverageRadius` (the MIC's own
    // radius, a different value) happened to also change. Since that doesn't track 1:1 with every
    // acoustics transition, switching acoustics could leave spkrPath (and micPath) stale — showing
    // whichever tier last coincided with a micCoverageRadius change, not the current one. This was
    // most visible right after loading a design: the very first render's spkrPath reflected
    // whatever tier the memo happened to compute for, and switching back to the design's own
    // acoustics tier later produced a DIFFERENT (correct) path than the initial, stale one.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [corner, leftCorner, rightCorner, length, bow, width, tableCenter.x, tableCenter.y, microphoneModel, micCoverageRadius, currentAcoustics, spkrCoverageRadius, isExpansionTableMic]);

  const active = useSelector((state: RootState) => getConditionResult(state, { ...props.active, area: areaId }));
  const currentView = useSelector((state: RootState) => state.ui.currentView);
  
  // Move handlers for the table
  const moveHandlers = {
    onPointerDown: (e: React.PointerEvent) => {
      if (!moveIconRef.current) return;
      moveIconRef.current.setPointerCapture(e.pointerId);
      
      // Get the current mouse position and calculate offset from current table position
      const currentTableX = tableTransform.x + tableCenter.x;
      const currentTableY = tableTransform.y + tableCenter.y;
      
      // The mouse hook already provides SVG coordinates, so we use those
      if (mouse) {
        setOffset({ x: mouse.x - currentTableX, y: mouse.y - currentTableY });
      }
      
      setMoving(true);
      dispatch(ReducersIndex.uiReducers.setAction('moveTable'));
      e.stopPropagation();
    },
    onPointerMove: (e: React.PointerEvent) => {
      if (!moving) return;
      moveTable();
    },
    onPointerUp: (e: React.PointerEvent) => {
      if (!moveIconRef.current) return;
      moveIconRef.current.releasePointerCapture(e.pointerId);
      setMoving(false);
      dispatch(ReducersIndex.uiReducers.setAction());
      // Save the new table position
      const newTransform = {
        x: round((tableTransform.x - bbox.cx + tableCenter.x) / scale, 4),
        y: round((tableTransform.y - bbox.cy + tableCenter.y) / scale, 4),
        a: currentTable.transform.a.toString()
      };
      dispatch((ReducersIndex.container as any).setTable({ transform: newTransform }));
    }
  };

  // Helper function to check if rotated table stays within bounds
  const isRotatedTableWithinBounds = (rotation: number, posX?: number, posY?: number): boolean => {
    // Use provided position or current position
    const centerX = posX ?? (tableTransform.x + tableCenter.x);
    const centerY = posY ?? (tableTransform.y + tableCenter.y);
    
    // Convert rotation to radians
    const rad = rotation * Math.PI / 180;
    const cos = Math.abs(Math.cos(rad));
    const sin = Math.abs(Math.sin(rad));
    
    // Get actual table dimensions
    const halfLength = tableSize.length / 2;
    const halfWidth = tableSize.width / 2;
    
    // Calculate rotated bounding box dimensions
    let rotatedHalfWidth, rotatedHalfHeight;
    
    if (currentTable.shape === 'ushape') {
      // For U-shape tables, use standard dimensions
      rotatedHalfWidth = halfLength * cos + halfWidth * sin;
      rotatedHalfHeight = halfLength * sin + halfWidth * cos;
    } else if (currentTable.shape === 'vshape') {
      // For V-shape tables, use standard dimensions
      rotatedHalfWidth = halfLength * cos + halfWidth * sin;
      rotatedHalfHeight = halfLength * sin + halfWidth * cos;
    } else if (currentTable.shape === 'bullet' || currentTable.shape === 'keystone') {
      // For bullet and keystone, use standard calculation
      // These shapes work better with standard boundaries for rotation
      rotatedHalfWidth = halfLength * cos + halfWidth * sin;
      rotatedHalfHeight = halfLength * sin + halfWidth * cos;
    } else if (currentTable.shape === 'round' || (currentTable.shape && (currentTable.shape.toLowerCase().includes('circle') || currentTable.shape.toLowerCase().includes('round')))) {
      // For round/circular tables, use the radius (they fit in a circle)
      const radius = Math.max(halfLength, halfWidth);
      rotatedHalfWidth = radius;
      rotatedHalfHeight = radius;
    } else {
      // For rectangular tables, use the standard calculation
      rotatedHalfWidth = halfLength * cos + halfWidth * sin;
      rotatedHalfHeight = halfLength * sin + halfWidth * cos;
    }
    
    // Check if the rotated bounding box fits within the room boundaries
    // Account for special tables that can get closer to walls
    let leftWallAdjustment = 0;
    if (currentTable.shape === 'ushape' || currentTable.shape === 'vshape' || 
        currentTable.shape === 'bullet' || currentTable.shape === 'keystone') {
      // These tables can get closer to the left wall when facing right (0-45° or 315-360°)
      const normalizedAngle = Math.abs(rotation % 360);
      if (normalizedAngle < 45 || normalizedAngle > 315) {
        leftWallAdjustment = currentTable.shape === 'ushape' ? 25 + 30 : table2wall;
      }
    }
    
    if (centerX - rotatedHalfWidth < bbox.x0 + table2wall - leftWallAdjustment || 
        centerX + rotatedHalfWidth > bbox.x1 - table2wall ||
        centerY - rotatedHalfHeight < bbox.y0 + table2wall || 
        centerY + rotatedHalfHeight > bbox.y1 - table2wall) {
      return false;
    }
    
    return true;
  };

  // Rotate handlers for the table
  const rotateHandlers = {
    onPointerDown: (e: React.PointerEvent) => {
      if (!rotateIconRef.current) return;
      rotateIconRef.current.setPointerCapture(e.pointerId);
      
      // Calculate initial angle using mouse position in SVG coordinates
      if (mouse) {
        const centerX = tableTransform.x + tableCenter.x;
        const centerY = tableTransform.y + tableCenter.y;
        const angle = Math.atan2(mouse.y - centerY, mouse.x - centerX) * 180 / Math.PI;
        const currentRotation = parseFloat(currentTable.transform.a) || 0;
        
        setRotationStart(angle - currentRotation);
      }
      
      setRotating(true);
      dispatch(ReducersIndex.uiReducers.setAction('rotateTable'));
      e.stopPropagation();
    },
    onPointerMove: (e: React.PointerEvent) => {
      if (!rotating || !mouse) return;
      
      // Calculate rotation angle
      const centerX = tableTransform.x + tableCenter.x;
      const centerY = tableTransform.y + tableCenter.y;
      const angle = Math.atan2(mouse.y - centerY, mouse.x - centerX) * 180 / Math.PI;
      const rotation = round(angle - rotationStart, 1);
      
      // Check if the rotated table would stay within bounds
      if (isRotatedTableWithinBounds(rotation, centerX, centerY)) {
        // Update table transform for visual feedback only if within bounds
        setTableTransfrom({
          ...tableTransform,
          a: `${rotation} ${tableCenter.x} ${tableCenter.y}`
        });
      }
    },
    onPointerUp: (e: React.PointerEvent) => {
      if (!rotateIconRef.current) return;
      rotateIconRef.current.releasePointerCapture(e.pointerId);
      setRotating(false);
      dispatch(ReducersIndex.uiReducers.setAction());
      
      // Save the new table rotation only if it's within bounds
      const currentRotation = parseFloat(tableTransform.a.split(' ')[0]);
      const centerX = tableTransform.x + tableCenter.x;
      const centerY = tableTransform.y + tableCenter.y;
      
      if (isRotatedTableWithinBounds(currentRotation, centerX, centerY)) {
        const newTransform = {
          x: currentTable.transform.x,
          y: currentTable.transform.y,
          a: currentRotation.toString()
        };
        dispatch((ReducersIndex.container as any).setTable({ transform: newTransform }));
      } else {
        // Reset to the previous valid rotation
        setTableTransfrom({
          ...tableTransform,
          a: `${currentTable.transform.a} ${tableCenter.x} ${tableCenter.y}`
        });
      }
    }
  };
  
  const moveTable = () => {
    let { x = 0, y = 0 } = mouse ?? {};
    if (!x || !y) return;
    
    // Calculate new position with offset
    x -= offset.x;
    y -= offset.y;
    
    // Get current rotation to check bounds properly
    const currentRotation = parseFloat(currentTable.transform.a) || 0;
    
    // For rotated tables, calculate the effective bounding box
    const rad = currentRotation * Math.PI / 180;
    const cos = Math.abs(Math.cos(rad));
    const sin = Math.abs(Math.sin(rad));
    
    // Calculate rotated bounding box dimensions
    const halfLength = tableSize.length / 2;
    const halfWidth = tableSize.width / 2;
    
    let rotatedHalfWidth, rotatedHalfHeight;
    let leftAdjustment = 0; // Extra space for left movement
    
    if (currentTable.shape === 'ushape') {
      // For U-shape tables, use standard dimensions
      rotatedHalfWidth = halfLength * cos + halfWidth * sin;
      rotatedHalfHeight = halfLength * sin + halfWidth * cos;
      
      // U-shape has opening on the left side - allow flush positioning
      const normalizedAngle = Math.abs(currentRotation % 360);
      if (normalizedAngle < 45 || normalizedAngle > 315) {
        // Facing right (opening on left) - allow flush left wall
        // Add adjustment to get closer - fine-tuned balance
        leftAdjustment = table2wall + 15;
      } else if (normalizedAngle > 135 && normalizedAngle < 225) {
        // Facing left (opening on right) - allow flush right wall
        // Note: we don't have rightAdjustment but this would be the logic
      }
    } else if (currentTable.shape === 'vshape') {
      // For V-shape tables, use standard dimensions
      rotatedHalfWidth = halfLength * cos + halfWidth * sin;
      rotatedHalfHeight = halfLength * sin + halfWidth * cos;
      
      // V-shape has opening on the left side - allow flush positioning
      const normalizedAngle = Math.abs(currentRotation % 360);
      if (normalizedAngle < 45 || normalizedAngle > 315) {
        // Facing right (opening on left) - allow flush with left wall
        // V-shape needs full adjustment to get close enough to wall
        leftAdjustment = table2wall;
      } else if (normalizedAngle > 135 && normalizedAngle < 225) {
        // Facing left (opening on right) - would need rightAdjustment
      }
    } else if (currentTable.shape === 'bullet' || currentTable.shape === 'keystone') {
      // For bullet and keystone, use standard dimensions for rotation calculation
      // but still allow some extra left movement
      rotatedHalfWidth = halfLength * cos + halfWidth * sin;
      rotatedHalfHeight = halfLength * sin + halfWidth * cos;
      
      // Allow moderate extra movement to the left for these shapes
      // Only apply when not significantly rotated
      if (Math.abs(currentRotation % 360) < 45 || Math.abs(currentRotation % 360) > 315) {
        // Use table2wall value directly since bullet/keystone have no chairs on left
        leftAdjustment = table2wall; // Allow flush positioning on left wall
      }
    } else if (currentTable.shape === 'round' || (currentTable.shape && (currentTable.shape.toLowerCase().includes('circle') || currentTable.shape.toLowerCase().includes('round')))) {
      // For round/circular tables, use the radius
      const radius = Math.max(halfLength, halfWidth);
      rotatedHalfWidth = radius;
      rotatedHalfHeight = radius;
    } else {
      // For rectangular tables, use the standard calculation
      rotatedHalfWidth = halfLength * cos + halfWidth * sin;
      rotatedHalfHeight = halfLength * sin + halfWidth * cos;
    }
    
    // Apply bounds with proper margins  
    // Adjust minX for special tables to allow movement closer to left wall (where opening is)
    let minX = bbox.x0 + rotatedHalfWidth + table2wall - leftAdjustment;
    const maxX = bbox.x1 - rotatedHalfWidth - table2wall;
    const minY = bbox.y0 + rotatedHalfHeight + table2wall;
    const maxY = bbox.y1 - rotatedHalfHeight - table2wall;
    
    
    // Ensure minX never goes below the absolute floor boundary
    // But allow special tables with leftAdjustment to get closer
    if (leftAdjustment === 0) {
      minX = Math.max(minX, bbox.x0 + rotatedHalfWidth);
    } else {
      // Even with leftAdjustment, ensure we never go outside the floorplan
      // For U-shape, use a balanced approach - allow closer but prevent overflow
      if (currentTable.shape === 'ushape') {
        // U-shape chairs might extend further than rotatedHalfWidth accounts for
        // Add extra margin to account for chair positioning
        const chairMargin = 30; // Extra pixels for chair clearance
        minX = Math.max(minX, bbox.x0 + rotatedHalfWidth * 0.5 + chairMargin);
      } else {
        minX = Math.max(minX, bbox.x0 + rotatedHalfWidth * 0.1);
      }
    }
    
    // Only clamp if the bounds are valid (max > min)
    if (maxX > minX) {
      x = clamp(x, minX, maxX);
    } else {
      // If bounds are invalid, at least keep within floor plan
      x = clamp(x, bbox.x0 + rotatedHalfWidth, bbox.x1 - rotatedHalfWidth);
    }
    
    if (maxY > minY) {
      y = clamp(y, minY, maxY);
    } else {
      // If bounds are invalid, at least keep within floor plan
      y = clamp(y, bbox.y0 + rotatedHalfHeight, bbox.y1 - rotatedHalfHeight);
    }
    
    // Update table transform for visual feedback
    setTableTransfrom({
      x: x - tableCenter.x,
      y: y - tableCenter.y,
      a: `${currentTable.transform.a} ${tableCenter.x} ${tableCenter.y}`
    });
  };
    // Prevent drawing the table if dimension are below minimum
  const hasValidSize = every(keys(currentTable.size), prop => currentTable.size[prop] >= minimumTableSize[prop]);
  const visible = Boolean(currentTable.shape) && hasValidSize;
  const filled = useSelector((state: RootState) => getConditionResult(state, { ...props.filled }));

  const tableRef = useRef<SVGGElement>(null);
  const { mode } = useTheme();
  const isDarkMode = mode === 'dark';
  const showHeatmap = useSelector((state: RootState) => state.ui?.showHeatmap);

  // Define table colors based on theme and current view
  const isFurnitureView = currentView === 'furnishing' || currentView === 'furniture';
  const isFloorPlanView = currentView === 'floor_plan' || currentView === 'floorplan';
  const isCeilingView = currentView === 'ceiling';
  
  // Determine border color based on view and theme
  let tableStroke: string;
  if (active) {
    tableStroke = '#007bff';  // Blue for active state
  } else if (isFurnitureView || isFloorPlanView || isCeilingView) {
    tableStroke = '#FFF';  // White borders for furniture/floorplan/ceiling views
  } else {
    // Grey borders in light mode, white borders in dark mode for other views (Acoustics, etc.)
    tableStroke = isDarkMode ? '#FFF' : '#6b7280';
  }
  
  // Determine table fill based on different conditions
  let tableFill: string;
  if (isFurnitureView) {
    // Use specific grey for furniture view
    tableFill = '#6b7280';
  } else if (isFloorPlanView || isCeilingView) {
    // Use different grey for floorplan and ceiling views
    tableFill = '#696969';
  } else {
    // For all other views (acoustics, microphones, speakers, cameras), use transparent fill
    tableFill = 'none';
  }

  return (
    <>
      {visible && (
        <g 
          id={`${areaId}-Furniture`} 
          ref={tableRef} 
          transform={`translate(${tableTransform.x}, ${tableTransform.y}) rotate(${tableTransform.a})`}
          strokeWidth="3px"
          shapeRendering="crispEdges"
        >
          {table.chairs.map((ch: ChairAttributes, i: number) => {
            const ghosted = currentTable.ghostChairs.includes(ch.id);
            let chairFillColor: string;
            let chairStrokeColor: string;
            
            if (isFurnitureView) {
              chairFillColor = '#6b7280';
              chairStrokeColor = '#6b7280'; // Remove white border - use same as fill
            } else if (isFloorPlanView || isCeilingView) {
              chairFillColor = '#696969';
              chairStrokeColor = '#696969'; // Remove white border - use same as fill
            } else {
              // For all other views (acoustics, microphones, speakers, cameras), use transparent fill
              chairFillColor = 'none';
              // Grey stroke color for outline-only appearance
              chairStrokeColor = '#6b7280';
            }
            const state = chair.state(ghosted, filled, chairFillColor, chairStrokeColor);
            // Ensure chairs have identical rendering properties as table
            const consistentState = { 
              ...state, 
              opacity: 1,
              chair_seat_fill: chairFillColor,
              chair_top_fill: chairFillColor === 'none' ? 'none' : chairFillColor,
              border_fill: chairFillColor
            };
            return (
              <g 
                key={ch.id} 
                opacity={1}
                fillOpacity={1}
                strokeWidth="0.5"
                strokeLinejoin="round"
                strokeLinecap="round"
                strokeMiterlimit="10"
                shapeRendering="geometricPrecision"
              >
                <Chair 
                  className="chair" 
                  state={consistentState} 
                  arms={true} 
                  size={chair.size} 
                  {...assignWith({ ...ch }, tableCenter, (a, b) => (a as number) + (b as number))} 
                />
              </g>
            );
          })}
          <path 
            d={table.path} 
            stroke={tableStroke}
            strokeWidth="1.6"
            fill={tableFill}
            opacity={1}
            fillOpacity={1}
            strokeLinejoin="round"
            strokeLinecap="round"
            pointerEvents="none"
          />
          <path 
            id={'micPath'} 
            d={table.micPath} 
            stroke="transparent"
            strokeWidth="0px"
            fill="none"
            shapeRendering="crispEdges"
            pointerEvents="none"
          />
          <path 
            id={'spkrPath'} 
            d={table.spkrPath} 
            stroke="transparent"
            strokeWidth="0px"
            fill="none"
            shapeRendering="crispEdges"
            pointerEvents="none"
          />
          {active && currentView === 'furnishing' && (() => {
            // REFACTORED APPROACH: Fixed button size that scales ONLY with viewport
            // NOT with individual table sizes - all tables get same size buttons
            
            // The 'scale' variable already represents the viewport/zoom scale
            // We want buttons to be a fixed size in the coordinate system
            // that will naturally scale when the viewport changes
            
            // Fixed button size for all table shapes
            const moveIconScale = 1.0;
            const rotateIconScale = 1.0;
            
            // Calculate table boundaries
            const tableLeft = tableCenter.x - (tableSize.length / 2);
            const tableRight = tableCenter.x + (tableSize.length / 2);
            const tableTop = tableCenter.y - (tableSize.width / 2);
            const tableBottom = tableCenter.y + (tableSize.width / 2);
            
            // Fixed distances for all table shapes
            const BUTTON_MARGIN = 35; // Distance from edge
            const BUTTON_SPACING = 40; // Spacing between buttons
            
            // Position buttons based on table shape for consistent appearance
            let buttonX, finalMoveButtonY, finalRotateButtonY;
            
            if (currentTable.shape && (currentTable.shape.toLowerCase().includes('circle') || currentTable.shape.toLowerCase().includes('round'))) {
              // Round tables: position at 45-degree angle from center, further out
              const radius = tableSize.length / 2;
              const buttonRadius = radius - BUTTON_MARGIN * 0.5; // Reduced margin for round tables
              const angle = Math.PI / 4; // 45 degrees
              buttonX = tableCenter.x + buttonRadius * Math.cos(angle);
              
              // Vertical positions for round table
              finalMoveButtonY = tableCenter.y - BUTTON_SPACING/2;
              finalRotateButtonY = tableCenter.y + BUTTON_SPACING/2;
            } else if (currentTable.shape === 'ushape') {
              // U-shape: buttons inside the U opening, further from edge
              buttonX = tableRight - BUTTON_MARGIN * 1.5; // Increased margin for U-shape
              finalMoveButtonY = tableCenter.y - BUTTON_SPACING/2;
              finalRotateButtonY = tableCenter.y + BUTTON_SPACING/2;
            } else if (currentTable.shape === 'vshape') {
              // V-shape: buttons at the open end of the V
              buttonX = tableRight - BUTTON_MARGIN;
              finalMoveButtonY = tableCenter.y - BUTTON_SPACING/2;
              finalRotateButtonY = tableCenter.y + BUTTON_SPACING/2;
            } else if (currentTable.shape === 'bullet' || currentTable.shape === 'keystone') {
              // Bullet/Keystone: buttons on the right side
              buttonX = tableRight - BUTTON_MARGIN;
              finalMoveButtonY = tableCenter.y - BUTTON_SPACING/2;
              finalRotateButtonY = tableCenter.y + BUTTON_SPACING/2;
            } else {
              // Rectangle, Racetrack, Boat, and other shapes: standard right-side positioning
              buttonX = tableRight - BUTTON_MARGIN;
              finalMoveButtonY = tableCenter.y - BUTTON_SPACING/2;
              finalRotateButtonY = tableCenter.y + BUTTON_SPACING/2;
            }
            
            // Enhanced safety bounds - keep buttons fully within table bounds
            const buttonSize = 16; // Approximate button icon size
            const safeMargin = buttonSize + 5; // Extra margin to ensure buttons don't touch edges
            
            const safeLeft = tableLeft + safeMargin;
            const safeRight = tableRight - safeMargin;
            const safeTop = tableTop + safeMargin;
            const safeBottom = tableBottom - safeMargin;
            
            // Constrain X position
            if (buttonX < safeLeft) buttonX = safeLeft;
            if (buttonX > safeRight) buttonX = safeRight;
            
            // Constrain Y positions
            if (finalMoveButtonY < safeTop) {
              finalMoveButtonY = safeTop;
              finalRotateButtonY = safeTop + BUTTON_SPACING;
            }
            
            if (finalRotateButtonY > safeBottom) {
              finalRotateButtonY = safeBottom;
              finalMoveButtonY = safeBottom - BUTTON_SPACING;
            }
            
            // Final check - if buttons would still be outside, adjust spacing
            if (finalMoveButtonY < safeTop) finalMoveButtonY = safeTop;
            if (finalRotateButtonY > safeBottom) finalRotateButtonY = safeBottom;
            
            return (
              <>
                <g 
                  className="action"
                  ref={moveIconRef}
                  transform={`translate(${buttonX}, ${finalMoveButtonY}) scale(${moveIconScale})`}
                  style={{ cursor: moving ? 'grabbing' : 'grab' }}
                  {...moveHandlers}
                >
                  <Move state={iconThemes['door_move']} center />
                </g>
                <g 
                  className="action"
                  ref={rotateIconRef}
                  transform={`translate(${buttonX}, ${finalRotateButtonY}) scale(${rotateIconScale})`}
                  style={{ cursor: rotating ? 'grabbing' : 'grab' }}
                  {...rotateHandlers}
                >
                  <Rotate state={iconThemes['door_move']} center />
                </g>
              </>
            );
          })()}
        </g>
      )}
    </>
  );
};

export default MuiTable;
